/**
 * PlannerAssignment.jsx — Atribuição de Project Manager e Operational Managers a planners.
 *
 * Duas secções:
 *  1. Planners por atribuir (sem PM) — destacados com alerta
 *  2. Todos os planners — tabela completa com PM e OMs
 */

import React, { useEffect, useState } from "react";
import {
  fetchAdminPlanners,
  fetchProjectManagers,
  fetchHeads,
  assignPlanner,
  bulkAssignPlanners,
  descobrirPlanners,
} from "../../services/api";

export default function PlannerAssignment() {
  const [planners, setPlanners] = useState([]);
  const [pms, setPms]   = useState([]);
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [formEdit, setFormEdit] = useState({ pm_ids: [], head_id: "" });
  const [salvando, setSalvando]         = useState(false);
  const [descobrindo, setDescobrindo]   = useState(false);
  const [resultadoDesc, setResultadoDesc] = useState(null);
  const [filtro, setFiltro]             = useState("todos");
  const [selecionados, setSelecionados] = useState([]);
  const [bulk, setBulk]                 = useState({ pm_id: "", head_id: "" });
  const [salvandoBulk, setSalvandoBulk] = useState(false);

  const carregar = () => {
    setLoading(true);
    Promise.all([fetchAdminPlanners(), fetchProjectManagers(), fetchHeads()])
      .then(([pl, pmList, headList]) => {
        setPlanners(pl);
        setPms(pmList);
        setHeads(headList);
      })
      .catch((e) => setErro(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, []);

  const lancarDescoberta = async () => {
    setDescobrindo(true);
    setResultadoDesc(null);
    setErro(null);
    try {
      const res = await descobrirPlanners();
      setResultadoDesc(res);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setDescobrindo(false);
    }
  };

  const semPM = planners.filter((p) => !p.project_manager_id);
  const listaMostrada = filtro === "semPM" ? semPM : planners;

  const iniciarEdicao = (planner) => {
    setEditandoId(planner.plan_id);
    setFormEdit({
      pm_ids:  (planner.project_managers || []).map((p) => p.id),
      head_id: planner.head_id ? String(planner.head_id) : "",
    });
  };

  const cancelarEdicao = () => { setEditandoId(null); setErro(null); };

  const guardar = async (plan_id) => {
    setSalvando(true);
    setErro(null);
    try {
      await assignPlanner(plan_id, {
        project_manager_ids: formEdit.pm_ids,
        head_id:             formEdit.head_id ? parseInt(formEdit.head_id) : null,
      });
      setEditandoId(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const togglePM = (pmId) => {
    setFormEdit((prev) => ({
      ...prev,
      pm_ids: prev.pm_ids.includes(pmId)
        ? prev.pm_ids.filter((id) => id !== pmId)
        : [...prev.pm_ids, pmId],
    }));
  };

  const toggleSelecionado = (planId) =>
    setSelecionados((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId]
    );

  const toggleTodos = () =>
    setSelecionados((prev) =>
      prev.length === listaMostrada.length ? [] : listaMostrada.map((p) => p.plan_id)
    );

  const guardarBulk = async () => {
    if (selecionados.length === 0) return;
    setSalvandoBulk(true);
    try {
      await bulkAssignPlanners({
        plan_ids:            selecionados,
        project_manager_ids: bulk.pm_id ? [parseInt(bulk.pm_id)] : [],
        head_id:             bulk.head_id ? parseInt(bulk.head_id) : null,
        atualizar_pm:        bulk.pm_id   !== "",
        atualizar_head:      bulk.head_id !== "",
      });
      setSelecionados([]);
      setBulk({ pm_id: "", head_id: "" });
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoBulk(false);
    }
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h2 style={styles.titulo}>Atribuição de Planners</h2>
          <p style={styles.subtitulo}>Descobre todos os planners da organização e atribui um Project Manager a cada um.</p>
        </div>
        <button
          style={{ ...styles.btnDescobrir, ...(descobrindo ? styles.btnDescobrirLoading : {}) }}
          onClick={lancarDescoberta}
          disabled={descobrindo}
        >
          {descobrindo ? "⏳ A descobrir…" : "🔍 Descobrir Planners"}
        </button>
      </div>

      {resultadoDesc && (
        <div style={styles.resultadoBanner}>
          ✅ Descoberta concluída em {resultadoDesc.duracao_segundos}s —{" "}
          <strong>{resultadoDesc.planners_encontrados}</strong> planners encontrados em{" "}
          <strong>{resultadoDesc.grupos_verificados}</strong> grupos,{" "}
          <strong>{resultadoDesc.planners_novos}</strong> novos adicionados.
        </div>
      )}

      <div style={styles.filtrosRow}>
        <button
          style={{ ...styles.filtroBtn, ...(filtro === "todos" ? styles.filtroBtnAtivo : {}) }}
          onClick={() => setFiltro("todos")}
        >
          Todos ({planners.length})
        </button>
        <button
          style={{ ...styles.filtroBtn, ...(filtro === "semPM" ? styles.filtroBtnAtivo : {}), ...(semPM.length > 0 ? styles.filtroBtnAlerta : {}) }}
          onClick={() => setFiltro("semPM")}
        >
          Por atribuir ({semPM.length})
        </button>
      </div>

      {/* Barra de atribuição em massa — aparece quando há planners selecionados */}
      {selecionados.length > 0 && (
        <div style={styles.bulkBar}>
          <span style={styles.bulkContagem}>
            {selecionados.length} planner{selecionados.length !== 1 ? "s" : ""} selecionado{selecionados.length !== 1 ? "s" : ""}
          </span>
          <select
            style={{ ...styles.bulkSelect, ...(bulk.pm_id ? {} : styles.bulkSelectVazio) }}
            value={bulk.pm_id}
            onChange={(e) => setBulk({ ...bulk, pm_id: e.target.value })}
          >
            <option value="">PM: não alterar</option>
            {pms.map((pm) => <option key={pm.id} value={pm.id}>{pm.nome}</option>)}
          </select>
          <select
            style={{ ...styles.bulkSelect, ...(bulk.head_id ? {} : styles.bulkSelectVazio) }}
            value={bulk.head_id}
            onChange={(e) => setBulk({ ...bulk, head_id: e.target.value })}
          >
            <option value="">Head: não alterar</option>
            {heads.map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
          </select>
          <button style={styles.btnBulk} onClick={guardarBulk} disabled={salvandoBulk}>
            {salvandoBulk ? "A guardar…" : "Atribuir Selecionados"}
          </button>
          <button style={styles.btnBulkCancelar} onClick={() => setSelecionados([])}>
            Cancelar
          </button>
        </div>
      )}

      {erro && <div style={styles.erro}>{erro}</div>}

      {semPM.length > 0 && filtro === "todos" && (
        <div style={styles.alertaBanner}>
          ⚠️ {semPM.length} planner{semPM.length !== 1 ? "s" : ""} sem Project Manager atribuído — não aparecem nos dashboards.
        </div>
      )}

      {loading ? (
        <p style={styles.loading}>A carregar…</p>
      ) : listaMostrada.length === 0 ? (
        <p style={styles.semDados}>
          {filtro === "semPM" ? "Todos os planners têm PM atribuído." : "Sem planners."}
        </p>
      ) : (
        <div className="table-scroll">
        <table style={styles.tabela}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 36, textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={selecionados.length === listaMostrada.length && listaMostrada.length > 0}
                  onChange={toggleTodos}
                  title="Selecionar todos"
                />
              </th>
              <th style={styles.th}>Planner / Marca</th>
              <th style={styles.th}>Project Manager</th>
              <th style={styles.th}>Head</th>
              <th style={{ ...styles.th, width: 100 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {listaMostrada.map((planner) => {
              const emEdicao = editandoId === planner.plan_id;
              return (
                <tr key={planner.plan_id} style={emEdicao ? styles.trEditando : selecionados.includes(planner.plan_id) ? styles.trSelecionado : planner.project_manager_id ? styles.tr : styles.trSemPM}>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selecionados.includes(planner.plan_id)}
                      onChange={() => toggleSelecionado(planner.plan_id)}
                    />
                  </td>
                  <td style={styles.td}>
                    <span style={styles.plannerNome}>{planner.plan_title}</span>
                    {planner.area && <span style={styles.area}>{planner.area}</span>}
                    {!planner.project_manager_id && !emEdicao && (
                      <span style={styles.badgeSemPM}>por atribuir</span>
                    )}
                  </td>

                  {emEdicao ? (
                    <>
                      <td />
                      <td style={styles.td}>
                        <div style={styles.omCheckboxes}>
                          {pms.map((pm) => (
                            <label key={pm.id} style={styles.omCheckLabel}>
                              <input
                                type="checkbox"
                                checked={formEdit.pm_ids.includes(pm.id)}
                                onChange={() => togglePM(pm.id)}
                                style={{ marginRight: 4 }}
                              />
                              {pm.nome}
                            </label>
                          ))}
                          {pms.length === 0 && (
                            <span style={{ fontSize: 12, color: "#94a3b8" }}>Sem PMs criados.</span>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>
                        <select
                          style={styles.selectInline}
                          value={formEdit.head_id}
                          onChange={(e) => setFormEdit({ ...formEdit, head_id: e.target.value })}
                        >
                          <option value="">— sem Head —</option>
                          {heads.map((h) => (
                            <option key={h.id} value={h.id}>{h.nome}</option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <button
                            style={styles.btnGuardar}
                            onClick={() => guardar(planner.plan_id)}
                            disabled={salvando}
                          >
                            {salvando ? "…" : "Guardar"}
                          </button>
                          <button style={styles.btnCancelar} onClick={cancelarEdicao}>
                            Cancelar
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={styles.td}>
                        {(planner.project_managers || []).length === 0
                          ? <span style={styles.semAtrib}>—</span>
                          : <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {planner.project_managers.map((pm) => (
                                <span key={pm.id} style={styles.pmTag}>{pm.nome}</span>
                              ))}
                            </div>
                        }
                      </td>
                      <td style={styles.td}>
                        {planner.head_nome
                          ? <span style={styles.headNome}>{planner.head_nome}</span>
                          : <span style={styles.semAtrib}>—</span>
                        }
                      </td>
                      <td style={styles.td}>
                        <button style={styles.btnEditar} onClick={() => iniciarEdicao(planner)}>
                          Editar
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 },
  titulo: { fontSize: 16, fontWeight: 600, margin: 0, color: "#1e293b" },
  subtitulo: { fontSize: 12, color: "#94a3b8", margin: "4px 0 0" },
  btnDescobrir: { padding: "10px 18px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" },
  btnDescobrirLoading: { background: "#a5b4fc", cursor: "not-allowed" },
  resultadoBanner: { background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: "10px 16px", fontSize: 13, color: "#166534", marginBottom: 16 },
  filtrosRow: { display: "flex", gap: 6, marginBottom: 16 },
  filtros: { display: "flex", gap: 6 },
  filtroBtn: { padding: "6px 14px", background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 500 },
  filtroBtnAtivo: { background: "#6366f1", color: "#fff", borderColor: "#6366f1", fontWeight: 700 },
  filtroBtnAlerta: { color: "#92400e", background: "#fef3c7", borderColor: "#fde68a" },
  alertaBanner: { background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 16px", fontSize: 13, color: "#92400e", marginBottom: 16 },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  tr: { borderBottom: "1px solid #f1f5f9" },
  trSemPM:      { borderBottom: "1px solid #f1f5f9", backgroundColor: "#fffbeb" },
  trEditando:   { borderBottom: "1px solid #f1f5f9", backgroundColor: "#f0f9ff" },
  trSelecionado:{ borderBottom: "1px solid #e0e7ff", backgroundColor: "#eef2ff" },
  bulkBar: {
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    background: "#eef2ff", border: "1px solid #c7d2fe",
    borderRadius: 8, padding: "12px 16px", marginBottom: 16,
  },
  bulkContagem: { fontSize: 13, fontWeight: 700, color: "#4338ca", marginRight: 4 },
  bulkSelect: { padding: "7px 10px", border: "1px solid #c7d2fe", borderRadius: 6, fontSize: 13, backgroundColor: "#fff", minWidth: 180 },
  bulkSelectVazio: { color: "#94a3b8", fontStyle: "italic" },
  btnBulk: { padding: "8px 18px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer" },
  btnBulkCancelar: { padding: "8px 14px", background: "none", color: "#6366f1", border: "1px solid #c7d2fe", borderRadius: 6, fontSize: 13, cursor: "pointer" },
  td: { padding: "10px 12px", color: "#374151", verticalAlign: "top" },
  plannerNome: { fontWeight: 500, display: "block" },
  area: { fontSize: 11, color: "#94a3b8", display: "block", marginTop: 2 },
  badgeSemPM: { display: "inline-block", marginTop: 4, padding: "1px 8px", background: "#fef3c7", color: "#92400e", borderRadius: 10, fontSize: 10, fontWeight: 600 },
  pmNome:   { fontWeight: 500 },
  pmTag:    { background: "#e0e7ff", color: "#4338ca", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 500 },
  headNome: { fontWeight: 500, color: "#7c3aed" },
  semAtrib: { color: "#cbd5e1" },
  selectInline: { width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13 },
  omCheckboxes: { display: "flex", flexDirection: "column", gap: 6 },
  omCheckLabel: { display: "flex", alignItems: "center", fontSize: 13, cursor: "pointer" },
  omTags: { display: "flex", gap: 4, flexWrap: "wrap" },
  omTag: { background: "#e0e7ff", color: "#4338ca", borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 500 },
  btnEditar: { padding: "4px 14px", background: "#e0e7ff", color: "#4338ca", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnGuardar: { padding: "5px 12px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnCancelar: { padding: "5px 12px", background: "none", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 12, cursor: "pointer" },
  semDados: { color: "#94a3b8", fontSize: 14 },
  loading: { color: "#64748b", fontSize: 14 },
  erro: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, color: "#dc2626", marginBottom: 16, fontSize: 13 },
};
